import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import useSeo from '@/hooks/useSeo';

const PAGE_SIZE = 6;

export default function News() {
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)),
    []
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useSeo({
    title: '吉泽明步影视动态 - 最新作品消息与深度解读',
    description:
      '吉泽明步影视动态，汇总最新作品消息、剧集收官资讯、深度影评解读与人物专访，帮助影迷持续了解影视动向。',
    keywords: '吉泽明步动态,影视动态,作品消息,影评解读,人物专访,影视新闻',
  });

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '动态' }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            影视动态
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            记录作品消息、行业活动与深度解读，持续跟进吉泽明步的影视脉络。
          </p>
        </div>
      </div>

      <div className="container-page py-8 md:py-10">
        {pageItems.length > 0 ? (
          <>
            <ul className="flex flex-col gap-5">
              {pageItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/${item.id}`}
                    className="group flex flex-col gap-5 rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer sm:flex-row sm:p-5"
                  >
                    <div className="w-full shrink-0 overflow-hidden rounded-md sm:w-56" style={{ aspectRatio: '16 / 10' }}>
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 影视动态`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                        <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-900">
                          {item.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" aria-hidden="true"></i>
                          {item.date}
                        </span>
                        <span className="hidden sm:block">{item.author}</span>
                      </div>
                      <h2 className="mt-3 font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                        {item.summary}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm text-primary-700 transition-all group-hover:gap-2">
                        阅读全文
                        <i className="ri-arrow-right-line" aria-hidden="true"></i>
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-newspaper-line text-2xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">暂无动态</h3>
            <p className="mt-2 text-sm text-foreground-600">目前还没有发布新的影视动态，敬请期待。</p>
          </div>
        )}
      </div>
    </div>
  );
}